import React, { useEffect, useMemo, useState } from 'react';
import './reviews-table.css';
import CompanyValueSummaryModal from './CompanyValueSummaryModal';

/**
 * Props:
 *  - items: Array<{ company_value: {id,name,weight,description?}, review: {...} }>
 *  - role: "employee" | "manager"  (controls which comment field is editable)
 *  - onSaveComment: async ({ id, value }) => void
 *  - contractId?: number
 *  - loadValueDetails?: (valueId: number) => Promise<object?>   // optional enrichment for modal
 */
export default function CompanyValuesSummaryTable({
                                                      items = [],
                                                      role = 'employee',
                                                      onSaveComment,
                                                      contractId,
                                                      loadValueDetails,
                                                  }) {
    // Local cache so table reflects changes immediately (optimistic)
    const [local, setLocal] = useState({});
    useEffect(() => {
        const seed = Object.fromEntries(
            (items || []).map(({ review }) => [review?.id, review])
        );
        setLocal(seed);
    }, [items]);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalValue, setModalValue] = useState(null);
    const [modalReview, setModalReview] = useState(null);

    // Strict numeric order by company_value.id
    const sections = useMemo(() => {
        const byId = new Map();
        for (const row of items || []) {
            const cv = row.company_value || {};
            const cvId = cv.id ?? `null-${row.review?.id}`;
            if (!byId.has(cvId)) {
                byId.set(cvId, { value: cv, rows: [] });
            }
            byId.get(cvId).rows.push(row);
        }
        const result = Array.from(byId.values());
        result.sort((a, b) => (Number(a.value?.id) || 0) - (Number(b.value?.id) || 0));
        // We expect one review row per value; normalize to { value, review }
        return result.map(({ value, rows }) => ({ value, review: (rows[0] || {}).review || {} }));
    }, [items]);

    const openForRole = (valueObj, reviewObj) => {
        setModalValue(valueObj);
        const cached = reviewObj?.id ? local[reviewObj.id] || reviewObj : reviewObj;
        setModalReview(cached);
        setModalOpen(true);
    };

    const handleSaveFromModal = async ({ id, value }) => {
        // optimistic
        setLocal(prev => {
            const current = prev[id] || {};
            const patch =
                role === 'employee'
                    ? { employee_comment: value }
                    : { manager_comment: value };
            return { ...prev, [id]: { ...current, ...patch } };
        });
        await onSaveComment({ id, value });
        setModalOpen(false);
    };

    const getReview = (review) => {
        if (!review?.id) return review || {};
        return local[review.id] || review;
    };

    return (
        <div className="card">
            <div className="card-header">
                <h6 className="mb-0">Company Values Summary</h6>
            </div>
            <div className="card-body">
                <div className="reviews-table mt-4">
                    <table>
                        <thead>
                        <tr className="rt-head-top">
                            <th className="rt-counter">#</th>
                            <th className="rt-name">Company Value Summary and Comments</th>
                            <th className="rt-role" style={{ width: 120 }}>Role</th>
                            <th className="rt-num" style={{ width: 90 }}>Rating</th>
                            <th className="rt-num" style={{ width: 120 }}>Description</th>
                            <th className="rt-comment-cell">Comment</th>
                            <th className="rt-actions" style={{ width: 120 }}>Action</th>
                        </tr>
                        </thead>

                        <tbody>
                        {sections.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center text-muted py-3">
                                    No company value summaries.
                                </td>
                            </tr>
                        )}

                        {sections.map((sec, i) => {
                            const counter = `CV ${String(i + 1).padStart(2, '0')}`;
                            const review = getReview(sec.review);
                            const managerRow = {
                                label: 'Manager',
                                rating: review?.manager_rating_id ?? '—',
                                desc: review?.manager_description ?? '—',
                                comment: review?.manager_comment ?? '',
                                canAct: role === 'manager' && !!review?.id,
                                onOpen: () => openForRole(sec.value, review),
                            };
                            const employeeRow = {
                                label: 'Employee',
                                rating: review?.employee_rating_id ?? '—',
                                desc: review?.employee_description ?? '—',
                                comment: review?.employee_comment ?? '',
                                canAct: role === 'employee' && !!review?.id,
                                onOpen: () => openForRole(sec.value, review),
                            };

                            return (
                                <React.Fragment key={String(sec.value?.id ?? i)}>
                                    {/* Manager row */}
                                    <tr className={`kpa-row ${i % 2 === 0 ? 'alt-row' : ''}`}>
                                        <td className="rt-counter" rowSpan={2}>
                                            {counter}
                                        </td>
                                        <td className="rt-name" rowSpan={2}>
                                            {sec.value?.name ?? '—'}
                                        </td>
                                        <td className="rt-num">{managerRow.label}</td>
                                        <td className="rt-num">{managerRow.rating}</td>
                                        <td className="rt-num">{managerRow.desc}</td>
                                        <td className="rt-comment-cell">
                                            {managerRow.comment || ''}
                                        </td>
                                        <td className="rt-actions">
                                            {managerRow.canAct ? (
                                                <button className="rt-btn rt-btn-primary" onClick={managerRow.onOpen}>
                                                    Open
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>

                                    {/* Employee row */}
                                    <tr className={`kpa-row ${i % 2 === 0 ? 'alt-row' : ''}`}>
                                        <td className="rt-num">{employeeRow.label}</td>
                                        <td className="rt-num">{employeeRow.rating}</td>
                                        <td className="rt-num">{employeeRow.desc}</td>
                                        <td className="rt-comment-cell">
                                            {employeeRow.comment || ''}
                                        </td>
                                        <td className="rt-actions">
                                            {employeeRow.canAct ? (
                                                <button className="rt-btn rt-btn-primary" onClick={employeeRow.onOpen}>
                                                    Open
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                        </tbody>
                    </table>

                    {/* Details Modal */}
                    <CompanyValueSummaryModal
                        show={modalOpen}
                        onClose={() => setModalOpen(false)}
                        role={role}
                        value={modalValue}
                        review={modalReview}
                        onSave={handleSaveFromModal}
                        loadValueDetails={loadValueDetails}
                        contractId={contractId}
                    />
                </div>
            </div>
        </div>
    );
}